import React, { useMemo } from 'react';
import { useTransition, animated } from '@react-spring/web';
import { createPortal } from 'react-dom';
import { createUseStyles, clsx } from '@pushpay/styles';
import { useResponsiveProp } from '@pushpay/theming';
import { getTooltipTransitionProps, getArrowStyles } from './helper';
const useStyles = createUseStyles(theme => ({
  root: ({
    placement,
    space
  }) => ({
    position: 'absolute',
    backgroundColor: 'currentColor',
    borderRadius: theme.SHAPE.ROUNDED_CORNERS,
    color: theme.colors['palette-grey-dark-300'],
    padding: theme.SPACING[space],
    width: 'fit-content',
    maxWidth: '240px',
    cursor: 'default',
    textAlign: placement === 'top' || placement === 'bottom' ? 'center' : 'left',
    zIndex: theme.ELEVATION.POPOVER.Z_INDEX
  }),
  content: {
    width: '100%',
    font: theme.TYPOGRAPHY.BODY_2,
    color: theme.colors['palette-white'],
    whiteSpace: 'pre-wrap',
    overflowWrap: 'break-word'
  },
  tooltipArrow: ({
    placement
  }) => ({ ...getArrowStyles(placement),
    position: 'absolute',
    width: 0,
    height: 0,
    border: '0.75em solid transparent',
    pointerEvents: 'none'
  })
}));
export const TooltipPanel = ({
  containerRef,
  visible,
  placement,
  space: spaceProp = 'XSMALL',
  animationDuration = 150,
  children,
  fixedContainer,
  classes: classesProp,
  className: classNameProp,
  ...rest
}) => {
  const space = useResponsiveProp(spaceProp);
  const classes = useStyles(classesProp, {
    placement,
    space
  });
  const className = clsx(classNameProp, classes.root);
  const transitionProps = getTooltipTransitionProps(containerRef, placement, fixedContainer);
  const transitionConfig = useMemo(() => ({
    clamp: true,
    duration: animationDuration
  }), [animationDuration]);
  const transitions = useTransition(visible, { ...transitionProps,
    config: transitionConfig
  });

  if (!transitionProps) {
    return null;
  }

  return /*#__PURE__*/createPortal(transitions((styles, item) => !!item && /*#__PURE__*/React.createElement(animated.div, Object.assign({
    role: "tooltip",
    className: className,
    style: styles
  }, rest), /*#__PURE__*/React.createElement("div", {
    className: classes.content
  }, children), /*#__PURE__*/React.createElement("span", {
    className: classes.tooltipArrow
  }))), document.body);
};